import { LogowallBlockItem } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.state'
import { BlocksView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.template'
import { Img } from '@shared/Frontend/components/img'
import { useDrag } from '@shared/Frontend/hooks/useDrag'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { Link } from '@shared/Frontend/utils/link'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BlockActions } from '../shared/blockActions'
import { BlockHeader } from '../shared/blockHeader'

// Exponential decay time constant (ms) for the momentum after releasing a
// drag: larger = the marquee keeps coasting for longer before settling
// back into its normal auto-scroll speed.
const MOMENTUM_DECAY_MS = 325
// Velocity (px/ms) below which momentum is considered settled.
const MOMENTUM_MIN_VELOCITY = 0.02

export const LogoWallBlock: BlocksView['LogoWallBlock'] = (props) => {
  const { id, items } = props
  if (items?.length == 0) return <></>

  const { inView, ref } = useInView({ threshold: 0.1 })

  const [isSettling, setIsSettling] = useState(false)
  const momentumFrame = useRef<number>()

  const stopMomentum = (): void => {
    if (momentumFrame.current != undefined) cancelAnimationFrame(momentumFrame.current)
    momentumFrame.current = undefined
    setIsSettling(false)
  }

  const handleDragStart = (): void => stopMomentum()

  const handleDragEnd = (velocity: number): void => {
    if (Math.abs(velocity) < MOMENTUM_MIN_VELOCITY) return
    setIsSettling(true)
    let currentVelocity = velocity
    let last = performance.now()

    const step = (now: number): void => {
      const dt = now - last
      last = now
      currentVelocity *= Math.exp(-dt / MOMENTUM_DECAY_MS)
      nudge(currentVelocity * dt)
      if (Math.abs(currentVelocity) < MOMENTUM_MIN_VELOCITY) {
        setIsSettling(false)
        momentumFrame.current = undefined
        return
      }
      momentumFrame.current = requestAnimationFrame(step)
    }

    momentumFrame.current = requestAnimationFrame(step)
  }

  const { ref: dragRef, offset, isDragging, nudge } = useDrag<HTMLDivElement>(handleDragEnd)

  useEffect(() => { if (isDragging) handleDragStart() }, [isDragging])
  useEffect(() => () => stopMomentum(), [])

  const listRef = useRef<HTMLUListElement>(null)
  const listWidth = listRef.current?.offsetWidth || 0
  const wrappedOffset = listWidth > 0 ? -(((-offset % listWidth) + listWidth) % listWidth) : 0

  const containerRef = useRef<HTMLDivElement>(null)
  const [copies, setCopies] = useState(2)

  useLayoutEffect(() => {
    const recompute = (): void => {
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const singleListWidth = listRef.current?.offsetWidth ?? 0
      if (containerWidth === 0 || singleListWidth === 0) return
      // (copies - 2) * listWidth must cover the container width.
      const needed = Math.max(2, Math.ceil(containerWidth / singleListWidth) + 2)
      setCopies((current) => (current === needed ? current : needed))
    }

    recompute()
    const observer = new ResizeObserver(recompute)
    if (containerRef.current) observer.observe(containerRef.current)
    if (listRef.current) observer.observe(listRef.current)
    return (): void => observer.disconnect()
  }, [items.length])

  return (
    <section id={id} ref={ref} className={cn('block block--logowall ls-spacing', inView && 'inview')}>
      <div className="block__inner ls-container ls-gutter">
        <BlockHeader {...props} headerClassName='block__header--centered' />

        <div className="block__content--full marquee" ref={containerRef}>
          <div
            ref={dragRef}
            className={cn('marquee__drag', isDragging && 'is-dragging', isSettling && 'is-settling')}
            style={{ transform: `translate3d(${wrappedOffset}px, 0, 0)` }}
          >
            <div
              className='marquee__slide'
              style={{ "--logos-count": items.length, "--logos-copies": copies } as React.CSSProperties}
            >
              <ListLayout items={items} clone={false} listRef={listRef} />
              {Array.from({ length: copies - 1 }).map((_, index) => (
                <ListLayout key={`clone-${index}`} items={items} clone={true} />
              ))}
            </div>
          </div>
        </div>

        <BlockActions {...props} datalayerCategory="logowall_block" footerClassName='block__footer--centered' />
      </div>
    </section>
  )
}

const ListLayout = (props: { items: LogowallBlockItem[]; listClassName?: string; clone: boolean; listRef?: React.RefObject<HTMLUListElement> }) => {
  if (props.items && props.items.length > 0) <></>
  return (
    <ul ref={props.listRef} className={cn('logos', props.listClassName)} aria-hidden={props.clone == true ? true : undefined}>
      {props.items.map((item, index) => {
        if (!item.logo) return <></>
        return (
          <li key={`logo-${index}`} className="logos__item">
            {item.link ? (
              <Link {...item.link} className="logos__link" draggable={false}>
                <InnerItemLayout {...item} />
              </Link>
            ) : (
              <div className='logos__wrap'>
                <InnerItemLayout {...item} />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

const InnerItemLayout = (props: LogowallBlockItem) => {
  const { logo, name } = props
  return (
    <figure className="logos__figure" title={name}>
      <div className="logos__visual">
        {logo && <Img {...logo} loading="lazy" className="logos__image" width="98" height="98" sizes="98px" draggable={false} />}
      </div>
      {name && <figcaption className="logos__caption sr-text">{name}</figcaption>}
    </figure>
  )
}
